/**
 * Timezone utility functions for the Guide app
 *
 * These helpers convert UTC timestamps to the guide's configured timezone
 * for display purposes while keeping UTC as the source of truth.
 */

/**
 * Format a UTC ISO string in a specific timezone
 *
 * @param utcIso - UTC ISO timestamp string (e.g., from scheduled_sessions.start_time)
 * @param timeZone - IANA timezone identifier (e.g., "America/New_York")
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date/time string in the specified timezone
 */
export function formatUtcInTimezone(
  utcIso: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions
): string {
  try {
    const date = new Date(utcIso);
    if (isNaN(date.getTime())) {
      return utcIso; // Fallback to raw value if invalid
    }
    return new Intl.DateTimeFormat('en-US', { timeZone, ...options }).format(date);
  } catch (error) {
    console.warn('formatUtcInTimezone error:', error);
    return utcIso;
  }
}

/**
 * Get the guide's timezone from basic_info, with browser fallback
 *
 * @param basicInfo - The guide's basic_info record from the database
 * @returns IANA timezone identifier
 */
export function getGuideTimezone(basicInfo?: { timezone?: string | null } | null): string {
  if (basicInfo?.timezone) {
    return basicInfo.timezone;
  }
  // Fallback to browser timezone
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/New_York'; // Final fallback
  }
}

/**
 * Format a time for display (e.g., "3:00 PM")
 */
export function formatTimeInTimezone(utcIso: string, timeZone: string): string {
  return formatUtcInTimezone(utcIso, timeZone, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format a date for display (e.g., "Monday, January 6, 2025")
 */
export function formatDateInTimezone(utcIso: string, timeZone: string): string {
  return formatUtcInTimezone(utcIso, timeZone, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a short date for display (e.g., "Jan 6")
 */
export function formatShortDateInTimezone(utcIso: string, timeZone: string): string {
  return formatUtcInTimezone(utcIso, timeZone, {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format date and time together (e.g., "Monday, Jan 6 at 3:00 PM")
 */
export function formatDateTimeInTimezone(utcIso: string, timeZone: string): string {
  const date = formatUtcInTimezone(utcIso, timeZone, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
  const time = formatTimeInTimezone(utcIso, timeZone);
  return `${date} at ${time}`;
}

/**
 * Get the date portion in YYYY-MM-DD format in a specific timezone
 */
export function getDateKeyInTimezone(utcIso: string, timeZone: string): string {
  // formatUtcInTimezone returns MM/DD/YYYY for en-US locale
  const formatted = formatUtcInTimezone(utcIso, timeZone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const [month, day, year] = formatted.split('/');
  return `${year}-${month}-${day}`;
}

/**
 * Check if a UTC timestamp is "today" in a given timezone
 */
export function isToday(utcIso: string, timeZone: string): boolean {
  try {
    const date = new Date(utcIso);
    const now = new Date();

    const dateInTz = formatUtcInTimezone(utcIso, timeZone, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    const todayInTz = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);

    return dateInTz === todayInTz;
  } catch {
    return false;
  }
}

/**
 * Check if a UTC timestamp is "tomorrow" in a given timezone
 */
export function isTomorrow(utcIso: string, timeZone: string): boolean {
  try {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const dateInTz = formatUtcInTimezone(utcIso, timeZone, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    const tomorrowInTz = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(tomorrow);

    return dateInTz === tomorrowInTz;
  } catch {
    return false;
  }
}

/**
 * Convert legacy date + time strings to a relative label
 * (Backward compatibility for sessions without start_time)
 */
export function formatLegacySessionTime(
  sessionDate: string,
  sessionTime: string,
  guideTimezone: string
): string {
  try {
    // Construct UTC-like timestamp from legacy fields
    const legacyTimestamp = `${sessionDate}T${sessionTime}`;
    const date = new Date(legacyTimestamp);

    if (isNaN(date.getTime())) {
      return `${sessionDate} ${sessionTime}`;
    }

    // For legacy data, we assume it was stored in the guide's local time
    // So we format it directly without timezone conversion
    return date.toLocaleString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return `${sessionDate} ${sessionTime}`;
  }
}

/**
 * Get the generic timezone abbreviation for a given UTC timestamp in a timezone
 * (e.g., "ET", "PT", "CT") — uses shortGeneric to avoid EST/EDT confusion.
 * Falls back to DST-specific abbreviation if shortGeneric is unsupported.
 */
export function getTimezoneAbbreviation(utcIso: string, timeZone: string): string {
  try {
    const date = new Date(utcIso);
    if (isNaN(date.getTime())) return '';
    // Try shortGeneric first (returns "ET" instead of "EST"/"EDT")
    try {
      const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'shortGeneric' }).formatToParts(date);
      const val = parts.find(part => part.type === 'timeZoneName')?.value;
      if (val) return val;
    } catch { /* shortGeneric not supported, fall through */ }
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' }).formatToParts(date);
    return parts.find(part => part.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/**
 * Get a time range string (e.g., "3:00 PM – 4:00 PM")
 */
export function formatTimeRangeInTimezone(
  startUtcIso: string,
  endUtcIso: string,
  timeZone: string
): string {
  const startTime = formatTimeInTimezone(startUtcIso, timeZone);
  const endTime = formatTimeInTimezone(endUtcIso, timeZone);
  return `${startTime} – ${endTime}`;
}
